import * as localForage from 'localforage';
import { isIOS, isBrowser } from './device-type';

const requestTimeout = 100;

// Use WebSQL for iOS because IndexedDB is broken on most iOS versions
if (isBrowser() && isIOS() && (window as any).openDatabase) {
  localForage.setDriver(localForage.WEBSQL);
}

export default (dbName: string) => {
  const storage = localForage.createInstance({
    storeName: dbName,
  });

  return {
    getItem<T>(key: string): Promise<T> {
      return Promise.race<any>([timeout(), init(dbName, storage).then(() => storage.getItem<T>(key))]).then(
        emptyIfTimeout,
      );
    },
    setItem<T>(key: string, value: T) {
      return Promise.race<any>([timeout(), init(dbName, storage).then(() => storage.setItem<T>(key, value))]).then(
        emptyIfTimeout,
      );
    },
    removeItem(key: string) {
      return Promise.race<any>([timeout(), init(dbName, storage).then(() => storage.removeItem(key))]).then(
        emptyIfTimeout,
      );
    },
    clear() {
      return Promise.race<any>([timeout(), init(dbName, storage).then(() => clearAndSetVersion(storage))]).then(
        emptyIfTimeout,
      );
    },
  };
};

const versionKey = 'local-db-version';
function clearAndSetVersion(storage: LocalForage) {
  return storage.clear().then(() => storage.setItem(versionKey, CURRENT_VERSION));
}

const initPromises: { [dbName: string]: Promise<any> } = {};
function init(dbName: string, storage: LocalForage) {
  if (!initPromises[dbName]) {
    initPromises[dbName] = storage.getItem<string>(versionKey).then<string | void>(v => {
      if (!v) {
        return clearAndSetVersion(storage);
      }
      const dbVersionParts = v.split('.');
      const currentVersionParts = CURRENT_VERSION.split('.');

      if (dbVersionParts[0] !== currentVersionParts[0]) {
        return clearAndSetVersion(storage);
      }
    });
  }
  return initPromises[dbName];
}

function emptyIfTimeout(item: any) {
  if (item && item.timeout === true) {
    console.debug('Timing out local db request because it took more than ' + requestTimeout + 'ms');
    return null;
  }
  return item;
}

function timeout() {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({ timeout: true });
    }, requestTimeout);
  });
}
